import {
  Affix,
  Badge,
  Dropdown,
  Row,
  Tabs,
  Tooltip,
  notification,
  Alert,
  Checkbox,
} from "antd";
import { ConfiguratorContext } from "../context";
import {
  AssemblyBase,
  BaseCategory,
  FRAME_SILL_LENGTH,
  Permission,
  Quote,
  QuoteAssemblyException,
  QuoteShare,
  AssemblyOption,
  QuoteRevisionDiff, CustomOptionType, TruckTags,
} from "../api/models";
import React, {useContext, useEffect, useRef, useState} from "react";
import {useParams} from "react-router-dom";
import useCheckMobileScreen from "../hook/useCheckMobileScreen";
import {NumberParam, StringParam, useQueryParam} from "use-query-params";
import { useIntl } from "react-intl";
import AssemblySectionMenu from "../components/Quote/AssemblySectionMenu";
import Title from "antd/lib/typography/Title";
import _ from "lodash";
import {
  WarningFilled,
  MoreOutlined,
} from "@ant-design/icons";
import QuoteHistoryTab from "../components/Quote/QuoteHistoryTab";
import Utils from "../util/util";
import {
  SaveDto
} from "../api";
import QuoteDashTab from "../components/Quote/QuoteDashTab";
import QuoteExportsDropDown from "../components/Quote/QuoteExportsDropDown";
import BMButton from "../components/BMButton";
import SubmitQuoteButton from "../components/Quote/SubmitQuoteButton";
import SubmitOrderButton from "../components/Quote/SubmitOrderButton";
import AbandonChangeOrderButton from "../components/Quote/AbandonChangeOrderButton";
import SetDealerPriceButtonModal from "../components/Quote/SetDealerPriceButtonModal";
import MoveTrucksButton from "../components/Quote/MoveTrucksButton";
import AssemblyExceptionButtonModal from "../components/Quote/AssemblyExceptionButtonModal";
import BomDetails from "../components/BomDetails";
import QuoteCopyModal from "../components/QuoteCopyModal";
import SplitOrder from "../components/Quote/SplitOrder";
import GenerateSerialButton from "../components/Quote/GenerateSerialButton";
import ArchiveQuoteSwitch from "../components/Quote/ArchiveButton";
import CancelOrderModule from "../components/Quote/CancelOrderModule";
import CreateChangeOrderButton from "../components/Quote/CreateSalesChangeOrderButton";
import PriceViewSwitch from "../components/Quote/PriceViewSwitch";
import { SegmentedValue } from "antd/es/segmented";
import WorkflowProgress from "../components/WorkflowProgress"
import CustomOptionsButtonModal from "../components/Quote/CustomOptionsButtonModal";
import ShareQuoteButtonModal from "../components/Quote/ShareQuoteButtonModal";
import QuoteContextProvider from "../contexts/QuoteContext";
import ConvertReservationButton from "../components/Quote/ConvertReservationButton";
import QuoteAuditView from "../components/Quote/QuoteAuditView";
import ReviseQuoteButton, { AbandonReviseQuoteButton } from "../components/Quote/ReviseQuoteButton";
import { ItemType } from "antd/es/menu/interface";
import RequestPoModalButton from "../components/RequestPoModalButton";
import { FEATURE_DOCUSIGN } from "../api/features";
import SubmitDocusignOrderButton from "../components/Quote/SubmitDocusignOrderButton";
import SubmitChangeOrderButton from "../components/Quote/SubmitChangeOrderButton";
import useComputedValid from "../swr/useComputedValid";
import useLatestApprovals from "../swr/useLatestApprovals";
import useModelCategories from "../swr/useModelCategories";
import useAssemblyExceptions from "../swr/useAssemblyExceptions";
import useQuoteHistory from "../swr/useQuoteHistory";
import useQuoteReview from "../swr/useQuoteReview";
import useQuote from "../swr/useQuote";
import RemoveSalesOrderButton from "../components/Quote/RemoveSalesOrder";
import ReleaseSerialsButton from "../components/Quote/ReleaseSerialsButton";
import RevertCancelButton from "../components/Quote/RevertCancelButton";
import AddPricingHistoryButton from "../components/Quote/AddPricingHistoryButton";
import useCustomOptions from "../swr/useCustomOptions";
import AssemblySelectionTable from "../components/Quote/AssemblySelectionTable";
import usePerformance from "../swr/usePerformance";
import PerformanceDetails from "../components/PerformanceDetails";
import {VersionConflictError} from "../api/errors";
import QuoteDetailsContents from "../components/Quote/QuoteDetailsContents";
import SaveConflictModal, {Conflict} from "../components/Quote/SaveConflictModal";
import QuoteSidePanel from "../components/Quote/QuoteSidePanel";
import DisableEpicorCheckbox from "../components/Quote/DisableEpicorCheckbox";
import InventoryCheckbox from "../components/Quote/InventoryCheckbox";
import QuoteHeader from "../components/Quote/QuoteHeader";
import AlertOldRevision from "../components/Quote/Alerts/AlertOldRevision";
import PricingViewPanel from "../components/Quote/PricingViewPanel";
import TotalPriceDisplay from "../components/Quote/TotalPriceDisplay";
import PendingSplitAlert from "../components/Quote/Alerts/PendingSplitAlert";
import AlertStock from "../components/Quote/Alerts/AlertStock";
import AlertExpired from "../components/Quote/Alerts/AlertExpired";
import AlertEngineerLock from "../components/Quote/Alerts/AlertEngineeringLock";
import AlertCancelled from "../components/Quote/Alerts/AlertCancelled";
import AlertEpicorSyncFailure from "../components/Quote/Alerts/AlertEpicorSyncFailure";
import AlertDuplicateSelections from "../components/Quote/Alerts/AlertDuplicateSelections";
import AlertRejected from "../components/Quote/Alerts/AlertRejected";

export const FORM_PERCENT_DISCOUNT = 'percentDiscount';

export const QUOTE_INFO_PANEL_KEY = "quote-info"
export const PERFORMANCE_PANEL_KEY = "performance-details"
export const DASH_PANEL_KEY = "dash-drawing"
export const HISTORY_PANEL_KEY = "history"
export const ASSEMBLY_PANEL_KEY = "assembly"

const SPEC_ONLY_VIEW = "Spec Only";
const SALES_PRICE_VIEW = "Sales";
const ADMIN_PRICE_VIEW = "Admin";

export function isAssembly(option: AssemblyBase | CustomOptionType | undefined) : boolean {
  if ( !option ) return false;
  return 'bom' in option;
}

const Configurator = () => {

  const configurator = useContext(ConfiguratorContext);
  const intl = useIntl();

  const isEngineering = configurator.isEngineering();
  const isAdmin = configurator.isAdmin();
  const isSalesDesk = configurator.isSalesDesk();
  const isDealer = configurator.isDealerSales();
  const isFinance = configurator.isFinance();
  const isSales = configurator.isInternalSales();
  const isDocusignEnabled = configurator.hasFeature(FEATURE_DOCUSIGN);
  const hasAdminView = configurator.isEngineering() || configurator.isSalesDesk() || configurator.isAdmin();

  const params = useParams<Record<string, string>>();
  const [revisionParam, _setRevisionParam] = useQueryParam<number | undefined | null >("rev", NumberParam);
  const [tabParam, setTabParam] = useQueryParam<string | undefined | null >("tab", StringParam);
  const [tabKey, setTabKey] = useState<string>(tabParam || QUOTE_INFO_PANEL_KEY);
  const [conflict, setConflict] = useState<Conflict>();

  const isMobile = useCheckMobileScreen( 1024 );
  const layoutStyle = isMobile ? { display: "flex" } : {};

  const [hidePrice, setHidePrice] = useState(false);  // Price hide to user ot not
  
  const [selectedCategory, setSelectedCategory] = useState<BaseCategory>();

  const [adminView, setAdminView] = useState<boolean>(hasAdminView); // AdminView is only for price detail to users
  const [optionNotes, setOptionNotes] = useState<Record<string, string>>({});
  const [filterOptionsQuery, setFilterOptionsQuery] = useState<string>();
  const [disableAutoselect, setDisableAutoselect] = useState<boolean>(false);
  const [disableAssemblyPricing, setDisableAssemblyPricing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const quoteAsync = useQuote({ quoteId: params.quoteId, revision: revisionParam || undefined });
  const quoteVersion = useRef<string>()
  const quote = quoteAsync.data;
  quoteVersion.current = quote?.version;

  const performanceAsync = usePerformance({ quote: quote });
  const performance = performanceAsync.data;

  const approvals = useLatestApprovals({
    quoteRevisionId: quote?.displayRevisionId,
  });

  const quoteHistory = useQuoteHistory({
    quoteId: quote?.quoteId,
    showAbandoned: false
  });

  const computedValid = useComputedValid({
    modelId: quote?.model.id,
    options: {
      quoteRevisionId: quote?.displayRevisionId,
    }
  });

  const review = useQuoteReview({
    quoteRevisionId: quote?.displayRevisionId,
    options: {
      dealerView: !adminView,
    }
  })

  const modelCategoriesAsync = useModelCategories({
    modelId: quote?.model.id,
    options: {
      quoteRevisionId: quote?.displayRevisionId,
      assemblyFilter: filterOptionsQuery,
      dealerView: !adminView,
    }
  })

  const customOptionsAsync = useCustomOptions({
    quoteRevisionId: quote?.displayRevisionId,
  })

  const modelCategories = modelCategoriesAsync.data
      ?.filter(cat => cat.name != "Default")
      .sort((a,b) => a.name.localeCompare(b.name));

  const selectedRevisionId = quote?.displayRevisionId || 0;

  const [sidePanelKeyParam, setSidePanelKeyParam] = useQueryParam<string | undefined | null >("side", StringParam);

  const defaultSidePanelKey = sidePanelKeyParam || "pricing-view";
  const [activeSidePanelKey, setActiveSidePanelKey] = useState<string | undefined>( isMobile ? undefined : defaultSidePanelKey);

  const sidePanelStyle = (activeSidePanelKey && !isMobile)
      ? {
        flex: "1 1 25rem",
        paddingLeft: "1rem"
      }
      : { flex: "0" };

  useEffect(() => {
    if ( !isMobile ) {
      if (hidePrice) {
        setActiveSidePanelKey(undefined);
      } else {
        setActiveSidePanelKey(defaultSidePanelKey);
      }
    }
  }, [hidePrice]);

  useEffect(() => {
    if ( quote ) {

      computedValid.mutate();
      review.mutate();
      quoteHistory.mutate();
      approvals.mutate();
      customOptionsAsync.mutate();
    }

  }, [quote]);

  useEffect(() => {
    if (selectedCategory) {
      handleTabClick(ASSEMBLY_PANEL_KEY);
    }
  }, [selectedCategory]);


  const assemblyExceptions = useAssemblyExceptions({quoteId: quote?.quoteId})

  const quoteState = Utils.getQuoteState(configurator, quote, quoteAsync.isLoading);
  const {
    isLatestRevision,
    isOrder,
    isDraft,
    isPending,
    isSplitOrder,
    isSalesChangeOrder,
    isEngineeringChangeOrder,
    isOrderShipped,
    isOrderCancelled,
    isApproved,
    isInitialLoading,
    isReviseQuote,
    isArchived,
    hasWritePermission,
    hasEngineeringChangePermission,
  } = quoteState;
  //console.log( "quoteState", quoteState );

  const isQuoteOwner = quote?.owner?.id === configurator.userInfo?.id;
  const isQuoteSales = quote?.salesTeam?.sales?.some(rep => rep.id === configurator.userInfo?.id);
  const isChangeOrder = isEngineeringChangeOrder || isSalesChangeOrder;

  const hasDealerReadPermission = configurator.hasAnyPermission([Permission.DEALER_ADMIN_READ, Permission.DEALER_MANAGEMENT_READ]);
  const hasSplitPermission =  configurator.hasPermission(Permission.CHANGE_ORDER_WRITE);
  const hasCancelOrderPermission = configurator.hasPermission(Permission.CANCEL_ORDER_WRITE);
  const isQuoteOnlyPermission = configurator.isQuoteOnly();
  const hasSubmitOrderPermission = (isSales || isDealer) && !isQuoteOnlyPermission;
  const hasSubmitQuotePermission = (isSales || isDealer);

  const isWorking =
    ( computedValid.isLoading || !computedValid.data ) ||
    modelCategoriesAsync.isLoading ||
    quoteAsync.isValidating || isSaving;

  const handleUpdateOptionNotes = (assemblyId:number | undefined, note:string | undefined) => {
    if ( !assemblyId ) return;

    setOptionNotes({ ...optionNotes, [assemblyId]:note});
  }

  const selectAssembly = (selected:Record<number, number[]> | undefined, category:BaseCategory, assemblyId: number ): number[] => {

    const categoryOptions = [...selected?.[ category.id ] || []];

    //toggle previously selected option
    if ( categoryOptions.find( id => id === assemblyId ) ) {
        return categoryOptions.filter( id => id !== assemblyId );
    }

    if ( !category.allowMultiple ) {
      return [ assemblyId ];
    }

    return [ ...categoryOptions, assemblyId];
  }

  const handleSelectCategory = (cat:BaseCategory | undefined) => {
    handleTabClick(ASSEMBLY_PANEL_KEY);
    setSelectedCategory(cat);
  }

  const handleSelectAssembly = async (category:BaseCategory,  option: AssemblyBase | CustomOptionType | undefined) => {
    if ( !category ) return;
    if ( !quote?.model ) return;

    const selectedOptions = quote.selections;
    const selectedCustomOptions = quote.selectedCustomOptions;

    const assembly = isAssembly(option) ? option as AssemblyOption : undefined;
    const customOption = !isAssembly(option) ? option as CustomOptionType : undefined;

    let options = {...selectedOptions};
    let customOptions = {...selectedCustomOptions};

    if ( assembly ) {

      //update assemblies
      const lst = selectAssembly( selectedOptions, category, assembly.id );
      options = {...selectedOptions, [ category.id ]: lst };

      //clear any custom options
      if ( !category.allowMultiple ) {
        customOptions = { ...selectedCustomOptions, [category.id]: [] };
      }
    }
    else if ( customOption ) {

      //update custom option
      const lst = selectAssembly(selectedCustomOptions, category, customOption.id ) || [];
      customOptions = {...customOptions, [ category.id ]: lst };

      //clear any assemblies
      if ( !category.allowMultiple ) {
        options = {...selectedOptions, [ category.id ]: [] };
      }
    }
    else {
      options = {...selectedOptions, [ category.id ]: [] };
      customOptions = { ...selectedCustomOptions, [category.id]: [] };
    }

    handleSaveQuote({
      selections: Object.values( options || {} ).flat().sort(),
      customOptions: Object.values( customOptions || {} ).flat().sort(),
    })

  }


  const existPerformanceError = () => {
    return !( !performance?.performanceWeight?.weightsMissing.length
    && !performance?.performanceWeight?.tareMissing.length
    && !performance?.performanceWeight?.gvwrMissing.length
    && !performance?.performanceData?.performanceMissing.length
    && !performance?.performanceDimension?.dimensionMissing.length );
  }


  const handleQuoteChanged = async (saveDto:SaveDto | undefined) => {
    if ( !saveDto ) {
      await quoteAsync.mutate();
      return;
    }

    await handleSaveQuote(saveDto);
  }


  const handleTabClick = (key: string) => {
    setTabKey(key);
    const param = [ ASSEMBLY_PANEL_KEY, QUOTE_INFO_PANEL_KEY].includes(key) ? undefined : key;
    setTabParam(param)
  }

  const getPriceView = () => {
    if (isDealer) {
      return [SALES_PRICE_VIEW, SPEC_ONLY_VIEW];
    }
    else if (hasAdminView) {
      return [ADMIN_PRICE_VIEW, SALES_PRICE_VIEW, SPEC_ONLY_VIEW];
    }
    else {
      return [SALES_PRICE_VIEW, SPEC_ONLY_VIEW];
    }
  }

  const setPriceView = (value: SegmentedValue) => {
    if (String(value) === ADMIN_PRICE_VIEW) {
      setAdminView(hasAdminView);
      setHidePrice(false);
    }
    else if (String(value) === SALES_PRICE_VIEW) {
      setAdminView(false);
      setHidePrice(false);
    }
    else if (String(value) === SPEC_ONLY_VIEW) {
      setAdminView(hasAdminView);
      setHidePrice(true);
    }
  }

  /*
  const getQuoteRevisionById = (quote: Quote | undefined, id: number | undefined): Revision | undefined => {
    return quote?.revisions.find(r => r.id === id);
  };

    const revision = getQuoteRevisionById( quote, quote?.displayRevisionId );

    const quoteDescriptionItems:DescriptionsItemType[] = !quote ? [] : [
      {
        key: "revision",
        label: "Revision",
        children:<span>Rev. {quote.revision} {(quote.epicorRevision && quote.epicorRevision !== "-") ? `( Epicor ${quote.epicorRevision})` : undefined}</span>
      },
      {
        key: "partNumber",
        label: "Part Number",
        children:
            <>
              <span data-testid="partNumberString" >{quote.partNumberString || "NA"}</span>
              {quote.partNumberString != undefined && quote.partNumberString !== "" && <Paragraph copyable={{text: quote.partNumberString}}></Paragraph>}
            </>
      },
    ]

    if ( !!quote?.trucks?.length  ) {
      quoteDescriptionItems.push({
        key: "serialNumber",
        label: !(!quote || !quote.trucks?.length || !revision) ? "Serial Number" : "",
        children: <>
          <div style={{display: 'flex', gap: '.8rem', alignItems: "baseline", flexWrap: "wrap", paddingRight: "1rem" }}>
            <QuoteTrucksButtonModal  />
          </div>
        </>
      });
    }

    if( !isDealer && !!quote?.salesOrderNumber ) {
      quoteDescriptionItems.push({
        key: "salesOrderNumber",
        label: quote.salesOrderNumber && !isDealer ? "Epicor Sales Order" : "",
        children: quote.salesOrderNumber && !isDealer ? <span>{quote.salesOrderNumber}</span> : <></>
      })
    }
  */

  const isCategorySelectionValid = (category: BaseCategory) : boolean => {
    const selectedLst = quote?.selections?.[category.id] || [];
    const computedLst = computedValid?.[category.id] || [];
    return !!selectedLst?.length && _.isEqual([...selectedLst].sort(), [...computedLst].sort());
  };

  const allSelectedOrFrameSillLengthIsOnlyUnselectedCategory = () :boolean => {
    const allUnselected = modelCategories?.filter(c => !isCategorySelectionValid(c)) || [];
    //all are selected
    if ( allUnselected.length === 0 ) return true;
    //more than one category is unselected
    if ( allUnselected.length > 1 ) return false;
    //is single unselected category FRAME_SILL_LENGTH?
    return allUnselected[0].name.includes(FRAME_SILL_LENGTH);
  }

  const handleAddException = (quoteAssemblyException:QuoteAssemblyException) => {
    //automatically select an assembly on adding it
    const category = modelCategories?.find( c => c.id === quoteAssemblyException.assembly.categoryId );
    if ( category ) {
      handleSelectAssembly( category, quoteAssemblyException.assembly );
    }
  }
      
  const saveEngineeringLock = async (quoteId:number, lock:boolean) : Promise<Quote | undefined> => {
    if ( !quoteId ) return;

    try {
      //fetch new pricing
      const resp = await configurator.api.saveQuoteEngineeringLock(quoteId, lock);

      return resp.data;
    } catch (e:any) {
      const errorMsg = intl.formatMessage({ id: e.message });
      notification.error( { message: "Failed to lock quote. " + errorMsg });
    }

    return;
  }

  const handleEngineeringLock = async (lock:boolean) => {
    if ( !quote ) return;

    const savedQuote = await saveEngineeringLock( quote.id, lock );
    if ( savedQuote ) {
      await quoteAsync.mutate( saveEngineeringLock(quote.id, lock));
    }

  }

  const getDisabledSplitMsg = () : string | undefined => {

    return isOrderCancelled? "Order is cancelled."
    : (isSalesChangeOrder && isPending) ? "Change order in progress."
    : (isEngineeringChangeOrder && isPending) ? "Engineering change in progress."
    : (isSplitOrder && isPending) ? "Split order in progress."
    : isPending ? "Open approvals must be resolved."
    : !isLatestRevision ? "Must be latest revision."
    : undefined;
  }

  const getDisabledAbandonMsg = () => {
    return isInitialLoading ? "The quote has not loaded."
      : isWorking ? "Please wait till background work has completed."
      : ( isEngineeringChangeOrder && !hasEngineeringChangePermission ) ? "This is an engineering change and can only be changed by an engineer."
      : undefined;
  }

  const getDisabledSubmitMsg = () => {
    return isInitialLoading ? "The quote has not loaded."
      : quote?.expired ? "This quote is expired.  Revise the quote before resubmitting."
      : isWorking ? "Please wait till background work has completed."
      : ( isEngineeringChangeOrder && !hasEngineeringChangePermission ) ? "This is an engineering change and can only be changed by an engineer."
      : undefined;
  }

  const getDisabledReviseQuoteMsg = () => {
    return isInitialLoading ? "The quote has not loaded."
      : isWorking ? "Please wait till background work has completed."
      : undefined;
  }

  const getDisabledSubmitOrderMsg = () => {
    const submitMsg = getDisabledSubmitMsg();
    return !!submitMsg ? submitMsg
      : quote?.expired ? "This quote is expired.  Revise the quote before resubmitting."
      : undefined;
  }

  const getDisabledConvertReservationMsg = () => {
    return isInitialLoading ? "The quote has not loaded."
      : isWorking ? "Please wait till background work has completed."
      : undefined;
  }

  const getDisabledOptionsMsg = () => {
    return isInitialLoading ? "The quote has not loaded."
      //: formDirty ? "These options are not available while there are unsaved changes."
      : undefined;
  }

  const hasCopyPermission = ():boolean => {
    const hasPermission = configurator.hasPermission(Permission.COPY_QUOTE_WRITE);
    const isStock = !!quote?.stock || ( quote?.trucks?.filter(t => t.tags?.includes(TruckTags.Fgi) ).length || 0 ) > 0
    return isQuoteOwner || isQuoteSales || hasPermission || hasDealerReadPermission || isStock;
  }

  const hasConvertStockPermission = ():boolean => {
    return isAdmin || isSalesDesk;
  }

  const getChangeOrderDisabledMsg = () => {
    return isInitialLoading ? "Please wait for the quote to finish loading."
      : quote?.archived ? "Archived quote cannot be modified."
      : isWorking ? "Please wait till background work has completed."
      : undefined;
  }

  const getConvertToStockDisabledMsg = () => {
    return isOrderCancelled? "Order is cancelled."
    : (isSalesChangeOrder && isPending) ? "Change order in progress."
    : (isEngineeringChangeOrder && isPending) ? "Engineering change in progress."
    : (isSplitOrder && isPending) ? "Split order in progress."
    : isPending ? "Open approvals must be resolved."
    : !isLatestRevision ? "Must be latest revision."
    : undefined;
  }
 
  const getDisabledAssemblyExceptionsMsg = () : string | undefined => {

    return undefined;
  }

  const handleReviseQuote = async () => {
    const q = await reviseQuote();
    if ( q ) {
      quoteAsync.mutate(quote);
    }
  }
  const reviseQuote = async () : Promise<Quote | void> => {
    if ( !quote ) return;

    try {
      const resp = await configurator.api.reviseQuote(quote.id)

      notification.success({message:"Reverted to Quote"});

      return resp.data;
    } catch (e: any) {
      const errorMsg = intl.formatMessage({ id: e.message || e.response?.data.message });
      const msg = "Failed to revert quote. " + errorMsg;
      notification.error( { message: msg });
    }

    return;
  }

  const handleSaveQuote = async (dto:SaveDto) => {

    setIsSaving(true);

    var saved = await saveQuote({
      ...dto,
    });
    if ( saved ) {
      await quoteAsync.mutate(saved);
      notification.success({message:"Quote Saved."});
    }

    setIsSaving(false);

    return saved;
  }


  const saveQuote = async (dto:SaveDto) : Promise<Quote | undefined> => {
    if ( !quote ) return;

    try {
      const resp = await configurator.api.updateQuoteRevision(quote.displayRevisionId, quoteVersion.current, dto)

      return resp.data;
    } catch (e: any) {
      if ( e instanceof VersionConflictError ) {
        resolveConflict( dto );
      }
      else {

        const errorMsg = intl.formatMessage({id: e.message || e.response?.data.message});
        const msg = "Failed to save quote. " + errorMsg;
        notification.error({message: msg});
      }
    }

    return;
  }

  const resolveConflict = async (dto:SaveDto) => {

    //update the quote, regardless
    await quoteAsync.mutate();

    var preview = await previewSave(dto);
    if ( preview && !Utils.isEmptyDeep(preview)) {
      setConflict( {
        preview, dto
      } )
    }
    else {
      notification.success({message:"Quote Saved."});
    }

  }

  const previewSave = async (dto:SaveDto) : Promise<QuoteRevisionDiff | undefined> => {
    if ( !quote ) return;

    try {
      const resp = await configurator.api.previewSaveQuote(quote?.displayRevisionId, dto)

      return resp.data;
    } catch (e: any) {
      const errorMsg = intl.formatMessage({id: e.message || e.response?.data.message});
      const msg = "Failed to preview changes. " + errorMsg;
      notification.error({message: msg});
    }

    return;
  }

  const handleQuoteShareChange = async (shareLst:QuoteShare[] | undefined) => {
    const updatedQuote = { ...quote, quoteShares: shareLst } as Quote;
    if( updatedQuote ) {
      quoteAsync.mutate(updatedQuote)
    }
  }

  const reloadQuote = (q?:Quote)  => {
    quoteAsync.mutate(q);
  }

  const handleSubmission = (q?:Quote)  => {
    quoteAsync.mutate(q);
    approvals.mutate();
  }

  const optionActionItems = new Array<ItemType>();

  if (configurator.isAdmin() ) {

    const handleClearPriceMatch = async () => {
      if (!quote?.displayRevisionId) return;
      try {
        await configurator.api.clearPriceMatch(quote.displayRevisionId);
        reloadQuote();
        notification.success({ message: "Price match cleared." });
      } catch (e: any) {
        notification.error({ message: "Failed to clear price match." });
      }
    };

    var adminSubMenuChildren = [
      {
        key: "adminSubMenu-assemblyExceptions",
        label: <SetDealerPriceButtonModal
            targetPrice={quote?.poNumber?.amount || quote?.totalPrice}
            revisionId={quote?.displayRevisionId}
            onChange={reloadQuote} type="text"/>
      },
      ...( quote?.pricingBreakdown?.priceMatch ? [{
        key: "adminSubMenu-clearPriceMatch",
        label: <BMButton className="ghostBmButton" type="text"
            onClick={handleClearPriceMatch}>Clear Price Match</BMButton>
      }] : []),
      {
        key: "adminSubMenu-fixPricingHistory",
        label: <AddPricingHistoryButton
            onChange={reloadQuote} type="text"/>
      },
      {
        key: "adminSubMenu-moveTrucks",
        label: <MoveTrucksButton type="text"/>
      },
    ];

    if ( quote?.serialNumberStr && !quoteState.isOrder && !quoteState.isOrderShipped ) {
      adminSubMenuChildren.push(
          {
            key: "adminSubMenu-releaseSerials",
            label: <ReleaseSerialsButton type="text" className="ghostBmButton"
                                         onChange={() => {
                                           quoteAsync.mutate();
                                           quoteHistory.mutate();
                                         }} />
          },
      )
    }

    if ( quote?.cancelled ) {
      adminSubMenuChildren.push(
          {
            key: "adminSubMenu-revertCancel",
            label: <RevertCancelButton type="text"
                                       onChange={() => {
                                         quoteAsync.mutate();
                                         quoteHistory.mutate();
                                       }} >Revert Cancellation</RevertCancelButton>
          },
      )
    }

    optionActionItems.push( {
      key: "adminSubMenu",
      label: "Admin",
      children: adminSubMenuChildren,
    });
  }

  if (configurator.isAdmin() || configurator.isEngineering()) {
    optionActionItems.push( {
      key: "engineeringSubMenu",
      label: "Engineering",
      children: [
        {
          key: "engineeringSubMenu-customOptions",
          label:
            <CustomOptionsButtonModal
              type="text"
              selectedCustomOptions={quote?.selectedCustomOptions}
              disabled={!!getDisabledAssemblyExceptionsMsg()}
              onDisabledClick={() => Utils.notifyDisabled(getDisabledAssemblyExceptionsMsg())}
              onChange={() => reloadQuote()}
            />
        },
        {
          key: "engineeringSubMenu-assemblyExceptions",
          label:
            <AssemblyExceptionButtonModal
              type="text"
              disabled={!!getDisabledAssemblyExceptionsMsg()}
              onDisabledClick={() => Utils.notifyDisabled(getDisabledAssemblyExceptionsMsg())}
              onAdd={handleAddException}
            >
              Assembly Exceptions
              <Badge count={assemblyExceptions.data?.length} size="default" style={{marginLeft: ".2rem"}} />
            </AssemblyExceptionButtonModal>
        },
        {
          key: "engineeringSubMenu-engineerLock",
          label:
            <div style={{textAlign: "center"}}>
            <label >
              <Checkbox
                onChange={(e) => handleEngineeringLock(e.target.checked)}
                checked={!!quote?.lockedByEngineer}
                style={{padding:0}}
              />
              <span style={{marginLeft: "0.5rem"}}>
              Engineering Lock
              </span>
            </label>
            </div>
        },
        {
          key: "disableEpicor",
          label: <DisableEpicorCheckbox />
        }
      ]
    } as ItemType );
  }

  if (hasWritePermission) {
    optionActionItems.push( {
      key: "shareQuote",
      label: <ShareQuoteButtonModal type="text"
        className="ghostBmButton"
        onChange={handleQuoteShareChange}
      >Share Quote</ShareQuoteButtonModal>
    } );
  }

  if ( isSales ) {
    optionActionItems.push( {
      key: "bomDetails",
      label: <BomDetails
        type="text"
        selectedOptions={quote?.selections}
        canAccessAll={isEngineering || isAdmin || isFinance || isSalesDesk}
        pricingSnapshotId={quote?.pricingConfig?.pricingSnapshot?.id}
      />
    } );
  }

  if ( hasCopyPermission() ) {

    optionActionItems.push( {
      key: "copy",
      label: <QuoteCopyModal
        type="text"
        onFinished={(copy) => {
          window.open("/configurator/" + encodeURI(copy.quoteId), "_blank");
        }}
      />});
  }

  if ( isDocusignEnabled && ( isAdmin || isSalesDesk ) ) {
    optionActionItems.push( {
      key: "po-request",
      label: <RequestPoModalButton type="text" >Request PO</RequestPoModalButton>
    });
  }

  if ( ( isAdmin || isSalesDesk ) && !!quote?.salesOrderNumber ) {
    optionActionItems.push( {
      key: "clearSalesOrder",
      label: <RemoveSalesOrderButton type="text" onChange={reloadQuote} >Remove Sales Order</RemoveSalesOrderButton>
    });
  }

  if (isApproved && isLatestRevision ) {
    if ( hasSplitPermission && ( ( quote?.quantity || 0 ) > 1 )) {
      optionActionItems.push({
        key: "split",
        label: <SplitOrder
            type="text"
            className="ghostBmButton"
            quote={quote}
            disabled={!!getDisabledSplitMsg()}
            onDisabledClick={() => Utils.notifyDisabled(getDisabledSplitMsg())}
        />});
    }

    if ( ( isAdmin || isEngineering || isSalesDesk ) && !(isOrderShipped || isOrderCancelled) ) {
      optionActionItems.push({
        key: "generateSerialNumber",
        label: <GenerateSerialButton type="text" />
      });
    }

    if (hasCancelOrderPermission) {
      optionActionItems.push({
        key: "cancelOrder",
        label: <CancelOrderModule type="text" quote={quote}/>
      })
    }
  }

  if ( isOrder && hasConvertStockPermission() ) {
    optionActionItems.push( {
      key: "stock",
      label: <InventoryCheckbox disabled={!!getConvertToStockDisabledMsg()} />
    });
  }

  if ( ( isAdmin || isEngineering || isSalesDesk || isQuoteOwner || isQuoteSales ) ) {
    optionActionItems.push({
      key: "archive",
      label: <ArchiveQuoteSwitch />
    });
  }

  const quoteContext = { quote, quoteId:params.quoteId, revision:revisionParam || undefined, adminView, disableAutoselect, disableAssemblyPricing, hidePrice  };

  const tabLst =[
    {
      key: QUOTE_INFO_PANEL_KEY,
      label: "Quote Info",
      children:
          <div style={{display:"flex"}}>

            <div style={{ flex: "8 1 0" }}>
            <QuoteDetailsContents onQuoteChange={handleQuoteChanged} onChange={reloadQuote} />
            </div>

            <div style={sidePanelStyle} >
              <QuoteSidePanel
                  isMobile={isMobile}
                  activeKey={activeSidePanelKey}
                  onChangeActiveKey={(k) => {
                    setActiveSidePanelKey(k);
                    setSidePanelKeyParam(k);
                  }
                  }
                  PricingViewPanel={ !hidePrice ? <PricingViewPanel onQuoteChanged={handleQuoteChanged} /> : undefined}
              />
            </div>
          </div>
    },
    {
      key: ASSEMBLY_PANEL_KEY,
      label: "Truck Options",
      children:
          !!selectedCategory
              ? <AssemblySelectionTable
                  selectedCategory={selectedCategory}
                  filterOptionsQuery={filterOptionsQuery}
                  optionNotes={optionNotes}
                  onUpdateOptionNotes={handleUpdateOptionNotes}
                  onQuoteChanged={handleQuoteChanged}
                  onChange={reloadQuote}
              />
              : <Row justify={"center"}><Title level={5}>Select category to show assembly table.</Title></Row>
    },
    {
      key: PERFORMANCE_PANEL_KEY,
      label:
          <>
            <span>Performance</span>
            {existPerformanceError() && <Tooltip title={"Please review errors in Performance Details tab"}>
              <WarningFilled data-testid="performancePanelWarningIcon" style={{marginLeft: ".5rem", fontSize: "20px",  color: "orange" }} size={5}/>
            </Tooltip>}
          </>,
      children: <PerformanceDetails onChange={handleQuoteChanged} />,
    },
    {
      key: DASH_PANEL_KEY,
      label: <span>Dash Drawing</span>,
      children:
          <>
            {quote && <QuoteDashTab
                quote={quote}
            />}
          </>,
      forceRender: true,
    },
    {
      key: HISTORY_PANEL_KEY,
      label: "History",
      children:
          <QuoteHistoryTab
              tabKey={tabKey}
          />
    },
  ];

  if ( configurator.isAdmin() || configurator.isEngineering() || configurator.isSalesDesk() ) {
    tabLst.push( {
      key: "QuoteAuditTab",
      label: "Audit",
      children: <QuoteAuditView />
    })
  }

  return <div>
    <style>
      {`
      .ant-descriptions .ant-descriptions-row > th, .ant-descriptions .ant-descriptions-row > td {
        padding-bottom: 5px;
      }

      .ant-descriptions .ant-typography {
        a{
          user-select: text;
        }
      }
    `}
    </style>

    <QuoteContextProvider value={quoteContext}>
      <div style={layoutStyle}>
        <AssemblySectionMenu
            loading={isWorking}
            computedOptions={computedValid.data}
            filterQuery={filterOptionsQuery}
            updateFilterQuery={setFilterOptionsQuery}
            onCategoryChange={setSelectedCategory}
            onClickCategory={handleSelectCategory}
            onToggleAutoSelect={setDisableAutoselect}
            onToggleAssemblyPricing={setDisableAssemblyPricing}
        />
        {process.env.REACT_APP_PRODUCTION_ENV !== 'true' &&
            <Affix style={{  textAlign: "center", backgroundColor: "rgba(0, 0, 0, 0)"}}>
              <div style={{backgroundColor: "yellow",display: "inline-block", padding: ".4rem" }}> This is a non-production environment. </div>
            </Affix>
        }
        <div className="site-layout-background" style={{  marginLeft: isMobile ? "0px" : "75px" }}>

          <div style={{ display: 'flex', flexDirection: "column" }}>

            <div >

              {/* | Header   | */}
              {/* | Details  | */}
              <div style={{ display: 'flex', flexDirection: "column", gap: "1rem" }}>

                {/* | Header | Buttons | */}
                <div style={{ display: 'flex', justifyContent: "space-between", gap: "1rem"  }}>

                  <QuoteHeader onQuoteChanged={handleQuoteChanged} />

                  <div style={{ display: 'flex', gap: "1rem", flexDirection: "column" }}>

                    <PriceViewSwitch
                        options={getPriceView()}
                        defaultValue={getPriceView()[0]}
                        setPriceView={setPriceView}
                    />

                    {!hidePrice &&
                        <div >
                          <Title level={5} >
                            <TotalPriceDisplay quotePricingDetails={quote?.pricingBreakdown} />
                          </Title>
                        </div>}

                  </div>
                </div>

                {isPending && <div>
                  <WorkflowProgress
                      approvals={approvals.data}
                      hideDescription={true}
                      screenChangeWidthPx={0}
                  />
                </div>}

                <div style={{display:"flex", justifyContent:"space-between"  }} >

                  <div style={{display:"flex", gap:"0.5rem"  }} >
                    <QuoteExportsDropDown
                        selectedRevisionId={selectedRevisionId}
                        allSelected={allSelectedOrFrameSillLengthIsOnlyUnselectedCategory()}
                        disabled={isWorking}
                    />


                    <Dropdown trigger={["click"]}
                              disabled={!optionActionItems.length}
                              menu={{items:optionActionItems}}
                    >
                      {/* this div is to avoid a warning with strict mode */}
                      <div>
                        <BMButton
                            type="primary"
                            disabled={!!getDisabledOptionsMsg()}
                            onDisabledClick={() => Utils.notifyDisabled(getDisabledOptionsMsg())}
                            icon={<MoreOutlined/>}
                            data-testid="quote-options-btn"
                        >Options</BMButton>
                      </div>
                    </Dropdown>
                  </div>


                  <div style={{display:"flex", gap:"0.5rem"  }} >
                  {/* create change order */}
                  {(isOrder && isLatestRevision && !isOrderCancelled && ((isApproved && hasSubmitOrderPermission) )) &&
                      <CreateChangeOrderButton
                          disabled={!!getChangeOrderDisabledMsg()}
                          onDisabledClick={() => Utils.notifyDisabled(getChangeOrderDisabledMsg())}
                          onDone={handleSubmission}
                      >
                        Create Change Order
                      </CreateChangeOrderButton>}

                  {(isDraft && isChangeOrder ) &&
                      <AbandonChangeOrderButton
                          disabled={!!getDisabledAbandonMsg()}
                          onDisabledClick={() => Utils.notifyDisabled(getDisabledAbandonMsg())}
                          onDone={handleSubmission}
                      />}

                  {(isDraft && hasSubmitOrderPermission && isChangeOrder ) &&
                      <SubmitChangeOrderButton
                          disabled={!!getDisabledSubmitMsg()}
                          onDisabledClick={() => Utils.notifyDisabled(getDisabledSubmitMsg())}
                          onDone={handleSubmission}
                      />}

                  {(isDraft && isReviseQuote ) &&
                      <AbandonReviseQuoteButton
                          disabled={!!getDisabledAbandonMsg()}
                          onDisabledClick={() => Utils.notifyDisabled(getDisabledAbandonMsg())}
                          onChange={handleSubmission}
                      />}

                  {(isApproved && !isPending && !isOrder && !isOrderCancelled ) &&
                      <>
                        <ReviseQuoteButton
                            disabled={!!getDisabledReviseQuoteMsg()}
                            onDisabledClick={() => Utils.notifyDisabled(getDisabledReviseQuoteMsg())}
                            onClick={handleReviseQuote}
                        >
                          Revise Quote
                        </ReviseQuoteButton>

                        {(hasSubmitOrderPermission  && !isOrderCancelled && !quote?.reservation ) && <>
                          {isDocusignEnabled
                              ? <SubmitDocusignOrderButton
                                  disabled={!!getDisabledSubmitOrderMsg()}
                                  onDisabledClick={() => Utils.notifyDisabled(getDisabledSubmitOrderMsg())}
                                  onQuoteChanged={handleQuoteChanged}
                                  onDone={handleSubmission}
                              />
                              : <SubmitOrderButton
                                  disabled={!!getDisabledSubmitOrderMsg()}
                                  onDisabledClick={() => Utils.notifyDisabled(getDisabledSubmitOrderMsg())}
                                  onQuoteChanged={handleQuoteChanged}
                                  onDone={handleSubmission}
                              />}
                        </>}
                      </>}

                  {(isApproved && !isPending && !isOrder && hasSubmitOrderPermission && quote?.reservation ) &&
                      <ConvertReservationButton
                          disabled={!!getDisabledConvertReservationMsg()}
                          onDisabledClick={() => Utils.notifyDisabled(getDisabledConvertReservationMsg())}
                          onDone={handleSubmission}
                      />}

                  {(!isOrder && !isApproved && !isPending && hasSubmitQuotePermission ) &&

                      <>
                        {( configurator.isInternalSales() )
                            ? <Dropdown
                                trigger={["click"]}
                                menu={{items:[
                                    {
                                      key:"submitQuote",
                                      label: <SubmitQuoteButton
                                          type="text"
                                          disabled={!!getDisabledSubmitMsg()}
                                          onDisabledClick={() => Utils.notifyDisabled(getDisabledSubmitMsg())}
                                          onQuoteChanged={handleQuoteChanged}
                                          onDone={handleSubmission}
                                      />
                                    },
                                    {
                                      key:"submitQuoteReservation",
                                      label: <SubmitQuoteButton
                                          type="text"
                                          disabled={!!getDisabledSubmitMsg()}
                                          onDisabledClick={() => Utils.notifyDisabled(getDisabledSubmitMsg())}
                                          onDone={handleSubmission}
                                          onQuoteChanged={handleQuoteChanged}
                                          isReservation={true}
                                      />
                                    }
                                  ]}}
                            >
                              {/* this div is to avoid a warning with strict mode */}
                              <div>
                                <BMButton
                                    type="primary"
                                    disabled={!!getDisabledSubmitMsg()}
                                    onDisabledClick={() => Utils.notifyDisabled(getDisabledSubmitMsg())}
                                    icon={<MoreOutlined/>}
                                >Submit</BMButton>
                              </div>
                            </Dropdown>
                            : <SubmitQuoteButton
                                disabled={!!getDisabledSubmitMsg()}
                                onDisabledClick={() => Utils.notifyDisabled(getDisabledSubmitMsg())}
                                onQuoteChanged={handleQuoteChanged}
                                onDone={handleSubmission}
                            />}
                      </>}
                  </div>
                </div>


                {isArchived &&
                    <Alert type="error" message={<>
                      <div>This quote has been archived.  No changes are permitted.</div>
                    </>}
                    />}

                <PendingSplitAlert />

                <AlertStock quote={quote} />

                <AlertExpired onReviseQuote={handleReviseQuote} />

                <AlertEngineerLock engineer={quote?.lockedByEngineer} />

                <AlertCancelled />

                <AlertOldRevision />

                <AlertEpicorSyncFailure />

                <AlertDuplicateSelections categories={modelCategories} onQuoteChanged={() => reloadQuote()} />

                <AlertRejected />

                <SaveConflictModal value={conflict}
                                   onOk={(dto) => {
                                     handleSaveQuote(dto);
                                     setConflict(undefined);
                                   }}
                                   onClose={() => setConflict(undefined)} />

              </div>

            </div>


            <Tabs
                id={"quote-tabs"}
                style={{marginTop: "1rem"}}
                activeKey={tabKey}
                onTabClick={(key) => handleTabClick(key)}
                items={tabLst}
            />


          </div>

        </div>

      </div>
    </QuoteContextProvider>
  </div>;
}

export default Configurator;
